import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/auth";
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import { Button } from '@mui/material';
import { GridOn, ViewList } from "@mui/icons-material";
import { Delete, Edit, Visibility } from "@mui/icons-material";
import { Table } from "../../components/Table";
import { Loader } from "../../components/Loaders/loader";
import DeleteCell from "../../components/Layout/CellScope/DeleteCell/DeleteCell";

export const UserInventory = () => {
    const [viewMode, setViewMode] = useState("list");
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [inventory, setInventory] = useState([]);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [batteryId, setbatteryId] = useState("");
    const { role } = useAuth();
    const navigate = useNavigate();

    const handleOpen = () => {
        window.open("/auth/addCell", "_blank");
    };

    const editHandler = useCallback(
        (id) => {
            navigate(`/auth/addCell/${id}`);
        },
        [inventory],
    );

    const removeFromList = (id) => {
        const updatedData = inventory.filter((cell) => cell._id !== id);
        setInventory(updatedData);
    };


    const toggleViewMode = () => {
        setViewMode((prevMode) => (prevMode === "list" ? "grid" : "list"));
    };

    useEffect(() => {
        const fetchInventory = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(
                    `${process.env.BACKEND_URL}batteries/inventory`,
                    {
                        headers: {
                            Authorization: localStorage.getItem("token"),
                        },
                    }
                );
                console.log(response.data);
                setInventory(response.data);
                setIsLoading(false);
            } catch (error) {
                console.log(error);
                setIsLoading(false);
            }
        };
        fetchInventory();
    }, []);

    const columnDef = [
        {
            header: "Image",
            cell: (data) => {
                return (
                    <div className="flex gap-1 md:gap-2 align-center justify-center">
                        <img
                            src={`${process.env.BACKEND_URL}files/${data.row.original.cell_image}?thumb=1`}
                            alt="cellImage"
                            className="w-5 h-5 md:w-10 md:h-10"
                        />
                        <img
                            src={`${process.env.BACKEND_URL}files/barcode/${data.row.original.battery_id}`}
                            alt="barcode"
                            className="w-5 h-5 md:w-10 md:h-10"
                        />
                    </div>
                );
            },
        },
        {
            accessorKey: "meta.manufacturer",
            header: "Manufacturer",
        },
        {
            accessorKey: "meta.model",
            header: "Model",
        },
        {
            accessorKey: "meta.type",
            header: "Type",
        },
        {
            accessorKey: "electrical_properties.capacity",
            header: "Capacity",
        },
        {
            accessorKey: "electrical_properties.nominal_voltage",
            header: "Voltage",
        },
        {
            accessorKey: "meta.soh",
            header: "SoH",
        },
        {
            accessorKey: "meta.price",
            header: "Price",
        },
        {
            accessorKey: "actions",
            header: "Actions",
            cell: (data) => {
                return (
                    <div className="flex gap-2">
                        <Visibility
                            cursor="pointer"
                            onClick={() => handleShowDetails(data.row.original._id)}
                        >
                            Show Details
                        </Visibility>
                        <Edit
                            color="info"
                            cursor="pointer"
                            onClick={() => editHandler(data.row.original._id)}
                        >
                            Edit
                        </Edit>
                        <Delete
                            color="error"
                            cursor="pointer"
                            onClick={() => { setbatteryId(data.row.original._id), setOpenDeleteModal(true) }}
                        >
                            Delete
                        </Delete>
                    </div>
                );
            },
        },
    ];


    const handleShowDetails = (id) => {
        window.open(`/auth/cellStore/${id}`, "_blank");
    };

    const handleShowDetailsRow = (id) => {
        if (role !== "admin") {
            window.open(`/auth/cellStore/${id}`, "_blank");
        }
    };

    const gridDeleteHandler = (id) => {
        setbatteryId(id);
        setOpenDeleteModal(true);
    };

    return (
        <div className="text-white pt-4 flex flex-col gap-10 items-center justify-center">
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    <h1 className="text-xl text-[#e8442d] text-center md:text-2xl lg:text-4xl">
                        My Inventory
                    </h1>
                    {inventory && inventory.length > 0 ? (
                        <div className="flex flex-col gap-7 items-center justify-center w-[85%]">

                            <div className="w-[100%] flex justify-between flex-row-reverse">
                                <div className="w-[100%] flex flex-row items-center gap-5 my-0 md:my-1 justify-end">
                                    <Button
                                        variant="contained"
                                        className="md:w-[20%] w-[auto] text-xs md:text-sm"
                                        sx={{
                                            backgroundColor: "#e8442d",
                                            fontFamily: "Bai Jamjuree",
                                            "&:hover": {
                                                backgroundColor: "#e85d01",
                                            },
                                            "&:active": {
                                                backgroundColor: "#e85d01",
                                            },
                                        }}
                                        onClick={handleOpen}
                                    >
                                        Add Cell
                                    </Button>
                                </div>
                                <div className="flex items-center border border-gray-300 rounded-[50px] bg-[#f9f9f9] ml-7">
                                    <div className={`hover:bg-[#e8432d51] hover:text-white rounded-l-[50px] cursor-pointer ${viewMode === "grid" ? "bg-[#e8432d7b]" : ""}`}
                                        onClick={toggleViewMode}
                                    >
                                        <GridOn className="cursor-pointer my-3 mx-4" />
                                    </div>
                                    <div className="border-r border-gray-300 h-full"></div>
                                    <div className={`hover:bg-[#e8432d51] hover:text-white rounded-r-[50px] cursor-pointer ${viewMode === "list" ? "bg-[#e8432d7b]" : ""}`}
                                        onClick={toggleViewMode}
                                    >
                                        <ViewList className="cursor-pointer my-3 mx-4" />
                                    </div>
                                </div>
                            </div>
                            <Table
                                columns={columnDef}
                                currentPage={currentPage}
                                data={inventory}
                                loadingData={false}
                                onRowClick={handleShowDetailsRow}
                                setCurrentPage={setCurrentPage}
                                viewHandler={handleShowDetails}
                                editHandler={editHandler}
                                deleteHandler={gridDeleteHandler}
                                viewMode={viewMode}
                                isEditable={true}
                            />
                            <DeleteCell openDeleteModal={openDeleteModal} onDelete={removeFromList} batteryId={batteryId} setOpenDeleteModal={setOpenDeleteModal} />
                        </div>
                    ) : (
                        <div className="flex flex-col text-center h-[35vh] items-center justify-center border-2 border-[#e8432d4e] rounded-lg p-4 w-full md:w-1/2">
                            <HiOutlineExclamationCircle size='2em' className='text-[#e8442d] mb-2' />
                            No cells found in the inventory. <br />
                            <Button
                                variant="contained"
                                className="md:w-[30%] w-[80%] mt-3"
                                sx={{
                                    backgroundColor: "#e8442d",
                                    fontFamily: "Bai Jamjuree",
                                    "&:hover": {
                                        backgroundColor: "#e85d01",
                                    },
                                    "&:active": {
                                        backgroundColor: "#e85d01",
                                    },
                                }}
                                onClick={handleOpen}
                            >
                                Add New Cell
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};